import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { verifiedCredentials } from '@/content/portfolio'
import { Toolkit } from '@/components/sections/cv/Toolkit'
import { useReducedMotion } from '@/lib/useReducedMotion'

export function AboutLearning() {
  const reducedMotion = useReducedMotion()
  return (
    <>
      <section id="learning" className="scroll-mt-24 border-t border-vibe-border py-16 lg:py-24">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 md:grid-cols-[1.2fr_1fr] lg:gap-24 lg:px-8">
          <div>
            <div className="section-label mb-5">04 / How I learn</div>
            <h2 className="text-3xl leading-tight text-text-heading lg:text-5xl">“Principles are far more important than education.”</h2>
            <p className="mt-6 leading-relaxed text-text-secondary">I care more about understanding why something works than memorizing the answer. I break problems down to their fundamentals, then try to explain them simply. If I can’t explain it, I probably don’t understand it yet.</p>
          </div>
          <div className="space-y-7 md:pt-10">
            <motion.div initial={reducedMotion ? false : 'hidden'} animate={reducedMotion ? 'visible' : undefined} whileInView="visible" viewport={{ once: true, amount: 0.7 }} className="border-y border-vibe-border py-6">
              <svg viewBox="0 0 360 48" aria-hidden="true" className="w-full overflow-visible">
                <path d="M 60 24 H 300" fill="none" className="stroke-vibe-border-strong" />
                <motion.path d="M 60 24 H 300" fill="none" className="stroke-accent-primary" variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1 } }} transition={{ duration: reducedMotion ? 0 : 1.2, ease: 'easeInOut' }} />
                {[60, 180, 300].map((cx, index) => (
                  <motion.circle key={cx} cx={cx} cy="24" r="5" className="fill-accent-primary" variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }} transition={{ duration: reducedMotion ? 0 : 0.2, delay: reducedMotion ? 0 : index * 0.6 }} />
                ))}
                <motion.circle cy="24" r="3" className="fill-text-heading" variants={{ hidden: { cx: 60 }, visible: { cx: 300 } }} transition={{ duration: reducedMotion ? 0 : 1.2, ease: 'easeInOut' }} />
              </svg>
              <ol className="grid grid-cols-3 font-mono text-xs text-text-secondary" aria-label="Learning sequence">
                {['Question', 'Understand', 'Explain'].map(step => <li key={step} className="text-center">{step}</li>)}
              </ol>
            </motion.div>
            <p className="leading-relaxed text-text-secondary"><span className="text-text-primary">First principles</span> help me question assumptions. <span className="text-text-primary">The Feynman technique</span> helps me find the gaps in my understanding.</p>
            <div><h3 className="text-xl text-text-heading">Away from the backlog</h3><p className="mt-3 leading-relaxed text-text-secondary">Daily walks, podcasts, and questions that go beyond product work. I write about systems thinking, AI, learning, and philosophy.</p><Link to="/blog" className="mt-4 inline-block text-sm text-accent-primary underline underline-offset-4">Read my writing ↗</Link></div>
          </div>
        </div>
      </section>
    </>
  )
}

export function AboutTools() {
  return (
    <>
      <Toolkit />
      <div className="mx-auto max-w-6xl px-5 py-8 lg:px-8">
        <details id="certifications" className="border-b border-vibe-border py-4"><summary className="cursor-pointer text-text-primary">Credentials</summary><ul className="mt-4 space-y-3 text-sm text-text-secondary">{verifiedCredentials.map(credential => <li key={credential}>{credential}</li>)}</ul></details>
      </div>
    </>
  )
}
